// Ported from QR/lib/qrRenderer.js (working POC). Renders a printable QR sticker
// (QR code + optional code text on up to 4 sides + a cutting-guide border) at a
// requested physical size, and lays multiple stickers out into a multi-page A4 PDF.
//
// Differences from the POC:
//  - size is parametrized (4x4cm or 8x8cm) instead of fixed at 8x8cm; all pixel
//    measurements scale proportionally from the physical size in mm.
//  - the PDF grid (columns/rows/gaps) is computed dynamically from the sticker size
//    instead of being hardcoded to a 2x3 layout.
//  - the QR itself encodes the full public URL of the code (buildUrl: buildDeviceQrUrl), not the
//    bare barcode - scanning with a phone camera must open the Kvalilog site.
//    The printed label text on the sticker still shows the short bare barcode though,
//    since a full URL would be too long to print legibly.
//  - the Kvalilog logo sits in the QR centre. qr-code-styling only reserves the area for it
//    (hideBackgroundDots); the logo pixels are composited here, see renderQrToCanvas.

/** Builds the URL a sticker's QR encodes for a code. */
export type QrUrlBuilder = (code: string) => string;

export type QrLabelSizeMm = 40 | 80; // 4x4cm or 8x8cm

export type QrTextSides = {
  top?: boolean;
  bottom?: boolean;
  left?: boolean;
  right?: boolean;
};

const DEFAULT_TEXT_SIDES: QrTextSides = { top: true, bottom: true, left: true, right: true };

// ~267 DPI - matches the original POC's 840px canvas for an 80mm (8cm) sticker.
const PX_PER_MM = 10.5;
const QR_LOGO_PATH = `${process.env.PUBLIC_URL}/logo.png`;
// Upper bound for one image load / QR render. Only there so a stalled load fails the
// generation with a visible error instead of leaving the modal spinning forever.
const LOAD_TIMEOUT_MS = 15000;

// Splits the code into groups of 3 characters (e.g. "ABC DEF GHI ADS") for the printed
// label only - the QR image itself still encodes the original, unspaced code/URL.
function formatCodeForDisplay(code: string): string {
  return code.replace(/(.{3})/g, '$1 ').trim();
}

function strokeRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
  ctx.stroke();
}

function withTimeout<T>(promise: Promise<T>, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(timeoutMessage)), LOAD_TIMEOUT_MS);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

// Loads `src` into an image element, rejecting on error or timeout instead of hanging.
function loadImageElement(src: string, description: string): Promise<HTMLImageElement> {
  return withTimeout(
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      // Keeps the canvas untainted should the logo ever be served from another (CORS-enabled) origin.
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Failed to load ${description}`));
      image.src = src;
    }),
    `Timed out loading ${description}`,
  );
}

type QrLogo = {
  image: HTMLImageElement;
  dataUrl: string;
};

let logoPromise: Promise<QrLogo> | null = null;

function imageToPngDataUrl(image: HTMLImageElement): string {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available');
  ctx.drawImage(image, 0, 0);
  return canvas.toDataURL('image/png');
}

// The logo is loaded once per session. A failed load is not cached, so the next attempt retries.
export function loadQrLogo(): Promise<QrLogo> {
  if (!logoPromise) {
    logoPromise = loadImageElement(QR_LOGO_PATH, `QR logo (${QR_LOGO_PATH})`)
      .then((image) => ({ image, dataUrl: imageToPngDataUrl(image) }))
      .catch((error) => {
        logoPromise = null;
        throw error;
      });
  }
  return logoPromise;
}

function buildQrOptions(qrData: string, qrSizePx: number, logoDataUrl: string) {
  return {
    width: qrSizePx,
    height: qrSizePx,
    // The SVG is rasterized by renderQrToCanvas; the library's own canvas/PNG export is not used.
    type: 'svg',
    data: qrData,
    margin: Math.max(2, Math.round(qrSizePx * 0.03)),
    qrOptions: { errorCorrectionLevel: 'H' },
    dotsOptions: { type: 'square', color: '#000000' },
    cornersSquareOptions: { type: 'square', color: '#000000' },
    cornersDotOptions: { type: 'square', color: '#000000' },
    backgroundOptions: { color: '#ffffff' },
    image: logoDataUrl,
    imageOptions: {
      hideBackgroundDots: true,
      imageSize: 0.42,
      margin: Math.max(2, Math.round(qrSizePx * 0.01)),
      crossOrigin: 'anonymous',
    },
  };
}


async function renderQrToCanvas(qrData: string, qrSizePx: number): Promise<HTMLCanvasElement> {
  const [ logo, { default: QRCodeStyling } ] = await Promise.all([ loadQrLogo(), import('qr-code-styling') ]);

  const qr = new QRCodeStyling(buildQrOptions(qrData, qrSizePx, logo.dataUrl) as any);
  const svgBlob = await withTimeout(qr.getRawData('svg'), 'Timed out rendering the QR code');
  if (!svgBlob) throw new Error('qr-code-styling returned an empty image');

  const svgDocument = new DOMParser().parseFromString(await svgBlob.text(), 'image/svg+xml');
  if (svgDocument.querySelector('parsererror')) throw new Error('qr-code-styling returned an invalid SVG');
  const logoPlaceholder = svgDocument.querySelector('image');
  if (!logoPlaceholder) throw new Error('qr-code-styling rendered the QR code without a logo area');

  const readLength = (name: string) => parseFloat(logoPlaceholder.getAttribute(name) ?? '');
  const logoX = readLength('x');
  const logoY = readLength('y');
  const logoWidth = readLength('width');
  const logoHeight = readLength('height');
  if (!(Number.isFinite(logoX) && Number.isFinite(logoY) && logoWidth > 0 && logoHeight > 0)) {
    throw new Error('qr-code-styling reserved no usable area for the logo');
  }
  logoPlaceholder.remove();

  const svgMarkup = new XMLSerializer().serializeToString(svgDocument.documentElement);
  const qrImage = await loadImageElement(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`,
    'the rendered QR code',
  );

  const canvas = document.createElement('canvas');
  canvas.width = qrSizePx;
  canvas.height = qrSizePx;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available');

  ctx.drawImage(qrImage, 0, 0, qrSizePx, qrSizePx);
  ctx.drawImage(logo.image, logoX, logoY, logoWidth, logoHeight);
  return canvas;
}

/** Renders one printable sticker at the requested physical size into a canvas element. */
export async function renderQrSticker(
  code: string,
  sizeMm: QrLabelSizeMm,
  buildUrl: QrUrlBuilder,
  textSides: QrTextSides = DEFAULT_TEXT_SIDES,
): Promise<HTMLCanvasElement> {
  const canvasSize = Math.round(sizeMm * PX_PER_MM);
  const borderPad = Math.round(canvasSize * (14 / 840));
  const textBand = Math.round(canvasSize * (58 / 840));
  const textGap = Math.round(canvasSize * (24 / 840));
  const qrOffset = borderPad + textBand + textGap;
  const qrSize = canvasSize - 2 * qrOffset;
  const fontSize = Math.max(12, Math.round(canvasSize * (44 / 840)));
  const font = `bold ${fontSize}px Verdana, "Segoe UI", Arial, sans-serif`;
  const displayCode = formatCodeForDisplay(code);

  const qrCanvas = await renderQrToCanvas(buildUrl(code), qrSize);

  const canvas = document.createElement('canvas');
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context is not available');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  ctx.drawImage(qrCanvas, qrOffset, qrOffset, qrSize, qrSize);

  const anySide = textSides.top || textSides.bottom || textSides.left || textSides.right;
  if (anySide) {
    ctx.font = font;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const cx = canvasSize / 2;
    const cy = canvasSize / 2;
    const tp = borderPad + textBand / 2;

    if (textSides.top) ctx.fillText(displayCode, cx, tp);
    if (textSides.bottom) ctx.fillText(displayCode, cx, canvasSize - tp);

    if (textSides.left) {
      ctx.save();
      ctx.translate(tp, cy);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(displayCode, 0, 0);
      ctx.restore();
    }

    if (textSides.right) {
      ctx.save();
      ctx.translate(canvasSize - tp, cy);
      ctx.rotate(Math.PI / 2);
      ctx.fillText(displayCode, 0, 0);
      ctx.restore();
    }
  }

  const borderWidth = Math.max(1, Math.round(canvasSize * (3 / 840)));
  const borderRadius = Math.round(canvasSize * (60 / 840));
  ctx.strokeStyle = '#aaaaaa';
  ctx.lineWidth = borderWidth;
  strokeRoundedRect(
    ctx,
    borderWidth / 2,
    borderWidth / 2,
    canvasSize - borderWidth,
    canvasSize - borderWidth,
    borderRadius,
  );

  return canvas;
}

/**
 * Lays out as many `sizeMm` x `sizeMm` stickers as fit on a portrait A4 page (evenly
 * spaced) and produces a multi-page PDF containing one sticker per code.
 */
export async function generateQrStickerPdf(
  codes: string[],
  sizeMm: QrLabelSizeMm,
  buildUrl: QrUrlBuilder,
  onProgress?: (pct: number) => void,
  textSides: QrTextSides = DEFAULT_TEXT_SIDES,
) {
  const { jsPDF } = await import('jspdf');

  const A4_WIDTH_MM = 210;
  const A4_HEIGHT_MM = 297;
  const cols = Math.max(1, Math.floor(A4_WIDTH_MM / sizeMm));
  const rows = Math.max(1, Math.floor(A4_HEIGHT_MM / sizeMm));
  const perPage = cols * rows;
  const hGap = (A4_WIDTH_MM - cols * sizeMm) / (cols + 1);
  const vGap = (A4_HEIGHT_MM - rows * sizeMm) / (rows + 1);

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  for (let i = 0; i < codes.length; i++) {
    const pos = i % perPage;
    if (pos === 0 && i > 0) pdf.addPage();

    const col = pos % cols;
    const row = Math.floor(pos / cols);
    const x = hGap + col * (sizeMm + hGap);
    const y = vGap + row * (sizeMm + vGap);

    const canvas = await renderQrSticker(codes[i], sizeMm, buildUrl, textSides);
    pdf.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', x, y, sizeMm, sizeMm, undefined, 'FAST');

    onProgress?.(Math.round(((i + 1) / codes.length) * 100));
  }

  return pdf;
}
